import { IStore } from '../interfaces';
import store from '@/study-search/store';
import { ParameterValue } from '../domain';
import { ParameterType, Currency } from '../enums';
import { BotHelper } from '../utils/BotHelper';
import { Filter } from '../models/filter';
import { EducationLevelFilterOptionValue } from '@studyportals/search-filters/server-side';
import { AreaFilterDefinition } from '../models/definition';

export class ParameterService {
	private store: IStore;
	private botHelper: BotHelper;

	public constructor() {
		this.store = store;
		this.botHelper = new BotHelper();
	}

	public getParameters(filters: readonly Filter[] = this.store.getters.getFilters()): ParameterValue[] {
		let parameterValues = this.generateParameterValues(filters, this.store.getters.getPortalType());
		parameterValues = this.addUserCountryParameterValue(parameterValues);
		parameterValues = this.addUserRegionParameterValue(parameterValues);

		return parameterValues;
	}

	private generateParameterValues(filters: readonly Filter[], portalType: string): ParameterValue[] {
		let parameterValues: ParameterValue[] = [];

		const applicableFilters = filters.filter((_) => _.type !== ParameterType.EDUCATION_LEVEL);
		for (const filter of applicableFilters) {
			switch (filter.type) {
				case ParameterType.COUNTRY:
					parameterValues = this.processCountryFilter(parameterValues, filter, filters);
					break;

				case ParameterType.KEYWORD_WHAT:
				case ParameterType.KEYWORD_WHERE:
					parameterValues = this.processKeywordFilter(parameterValues, filters);
					break;

				default:
					parameterValues = this.processDefaultFilter(parameterValues, filter);
			}
		}

		if (portalType as EducationLevelFilterOptionValue === EducationLevelFilterOptionValue.DISTANCE_LEARNING) {
			parameterValues.push(
				new ParameterValue(ParameterType.EDUCATION_LEVEL, this.store.getters.getFilterValues(ParameterType.EDUCATION_LEVEL)),
			);
		} else {
			parameterValues.push(new ParameterValue(ParameterType.EDUCATION_LEVEL, [portalType]));
		}

		parameterValues.push(new ParameterValue(ParameterType.CURRENCY, [Currency.EURO]));

		return parameterValues.filter((parameterValue) => parameterValue.value.length > 0);
	}

	private processDefaultFilter(parameterValues: ParameterValue[], filter: Filter): ParameterValue[] {
		parameterValues.push(new ParameterValue(filter.type, filter.values));
		return parameterValues;
	}

	private processKeywordFilter(parameterValues: ParameterValue[], filters: readonly Filter[]): ParameterValue[] {
		const kwParameter = parameterValues.find((_) => _.type === ParameterType.KEYWORD);

		if (!kwParameter) {
			const kwWhat = filters.find((_) => _.type === ParameterType.KEYWORD_WHAT);
			const kwWhere = filters.find((_) => _.type === ParameterType.KEYWORD_WHERE);

			if ((kwWhat && kwWhat?.values.length > 0) || (kwWhere && kwWhere?.values.length)) {
				const keywordParam = this.mergeKeywordsWhatAndWhere(kwWhat, kwWhere);

				parameterValues.push(keywordParam);
			}
		}

		return parameterValues;
	}

	private processCountryFilter(parameterValues: ParameterValue[], filter: Filter, filters: readonly Filter[]): ParameterValue[] {
		const processedValues = this.removeCountriesWithAreasSelected(
			filter.values,
			filters.find((_) => _.type === ParameterType.AREA),
		);
		parameterValues.push(new ParameterValue(filter.type, processedValues));
		return parameterValues;
	}

	private addUserCountryParameterValue(parameterValues: ParameterValue[]): ParameterValue[] {
		const userCountryIso = this.store.getters.getUserCountryIso();
		if (this.botHelper.isBot() || this.botHelper.isPrerender() || !userCountryIso) {
			return parameterValues;
		}

		parameterValues.push(new ParameterValue(ParameterType.USER_COUNTRY, [userCountryIso]));

		return parameterValues;
	}

	private addUserRegionParameterValue(parameterValues: ParameterValue[]): ParameterValue[] {
		const isEeaCountry = this.store.getters.isEea();

		if (this.botHelper.isBot() || this.botHelper.isPrerender() || !isEeaCountry) {
			return parameterValues;
		}

		parameterValues.push(new ParameterValue(ParameterType.USER_REGION, ['38']));

		return parameterValues;
	}

	private removeCountriesWithAreasSelected(countryIds: string[], areaFilter: Filter | undefined): string[] {
		if (areaFilter === undefined) {
			return countryIds;
		}

		const processedCountryIds: string[] = [];
		const countryIdsToRemove = areaFilter.values.map((areaId) => AreaFilterDefinition.getCountryId(areaId));
		countryIds.forEach((countryId) => {
			if (!countryIdsToRemove.some((countryIdToRemove) => countryIdToRemove === countryId)) {
				processedCountryIds.push(countryId);
			}
		});

		return processedCountryIds;
	}

	private mergeKeywordsWhatAndWhere(kwWhat?: Filter, kwWhere?: Filter): ParameterValue {
		let value = '';

		if (kwWhat && kwWhat.values.length) {
			value = kwWhat.values[0];
		}

		if (kwWhere && kwWhere.values.length) {
			value = value + ' ' + kwWhere.values[0];
		}

		return new ParameterValue(ParameterType.KEYWORD, [value]);
	}
}
